"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";

import {
  DARK_QUERY,
  DEFAULT_THEME,
  THEME_STORAGE_KEY,
  type ResolvedTheme,
  type Theme,
} from "@/components/providers/preference-bootstrap";

export type { ResolvedTheme, Theme };
export { THEME_STORAGE_KEY };

export interface ThemeContextValue {
  /** What the user picked, including the `system` pass-through. */
  theme: Theme;
  /** What is actually on screen right now. */
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  /** Commits to the explicit opposite of what is currently displayed. */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function isTheme(value: unknown): value is Theme {
  return value === "light" || value === "dark" || value === "system";
}

// Two external stores: the persisted choice, and the OS preference it may defer
// to. Reading both through `useSyncExternalStore` keeps the server render
// deterministic and avoids a setState-in-effect cascade on every load.
const listeners = new Set<() => void>();

/** Keeps the choice usable when storage is blocked (private mode, sandboxed iframe). */
let memoryTheme: Theme | null = null;

function readTheme(): Theme {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (isTheme(raw)) return raw;
  } catch {
    // Fall through to the in-memory value.
  }
  return memoryTheme ?? DEFAULT_THEME;
}

function writeTheme(theme: Theme): void {
  memoryTheme = theme;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // The choice still holds for this tab.
  }
  // `storage` events fire in *other* tabs only, so this tab is notified here.
  for (const listener of listeners) listener();
}

function subscribeToTheme(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === THEME_STORAGE_KEY) onStoreChange();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(onStoreChange);
    window.removeEventListener("storage", onStorage);
  };
}

function readServerTheme(): Theme {
  return DEFAULT_THEME;
}

function subscribeToSystemTheme(onStoreChange: () => void): () => void {
  const media = window.matchMedia(DARK_QUERY);
  media.addEventListener("change", onStoreChange);
  return () => media.removeEventListener("change", onStoreChange);
}

function readSystemTheme(): ResolvedTheme {
  return window.matchMedia(DARK_QUERY).matches ? "dark" : "light";
}

function readServerSystemTheme(): ResolvedTheme {
  return "light";
}

/**
 * Three switches on purpose: `globals.css` may key its dark palette off either
 * `.dark` or `[data-theme="dark"]`, and `color-scheme` is what makes native
 * scrollbars, form controls and the canvas colour follow along.
 */
function applyTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.dataset.theme = resolved;
  root.style.colorScheme = resolved;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const theme = useSyncExternalStore(subscribeToTheme, readTheme, readServerTheme);
  const systemTheme = useSyncExternalStore(
    subscribeToSystemTheme,
    readSystemTheme,
    readServerSystemTheme,
  );

  const resolvedTheme: ResolvedTheme = theme === "system" ? systemTheme : theme;

  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  const setTheme = useCallback((next: Theme) => {
    writeTheme(next);
  }, []);

  const toggleTheme = useCallback(() => {
    writeTheme(resolvedTheme === "dark" ? "light" : "dark");
  }, [resolvedTheme]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme, toggleTheme }),
    [theme, resolvedTheme, setTheme, toggleTheme],
  );

  // No bootstrap script here: it is rendered once from the root layout's
  // `<head>` by `PreferenceBootstrap`, which is early enough to beat the paint.
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) {
    throw new Error("useTheme must be used inside <ThemeProvider>.");
  }
  return value;
}

/**
 * Mounts a `ThemeProvider` only when the tree does not already have one, so a
 * route-group layout can guarantee the theme without two providers writing to
 * `<html>` from separate effects. Mirrors `EnsureAuthProvider`.
 */
export function EnsureThemeProvider({ children }: { children: ReactNode }) {
  const existing = useContext(ThemeContext);
  if (existing) return <>{children}</>;
  return <ThemeProvider>{children}</ThemeProvider>;
}
